import type { LedgerSafeUnitSplitContext } from './ledgerSafeBoundaryEditing'

export type LedgerSafeSplitSuggestionConfidence = 'high' | 'medium'

export interface LedgerSafeSplitLocalizationOptions {
  minimumScore?: number
  minimumMargin?: number
  highConfidenceScore?: number
  highConfidenceMargin?: number
}

export interface LedgerSafeSplitLocalizationInput extends LedgerSafeSplitLocalizationOptions {
  sourceContext: LedgerSafeUnitSplitContext
  targetContext: LedgerSafeUnitSplitContext
  sourceSplitBoundaryWordIndex: number
}

export interface LedgerSafeSplitLocalizationResult {
  afterWordIndex: number
  confidence: LedgerSafeSplitSuggestionConfidence
  score: number
  margin: number
  sourceBoundaryOffset: number
  targetBoundaryOffset: number
  sourceLeftText: string
  sourceRightText: string
  targetLeftText: string
  targetRightText: string
  metrics: {
    leftScore: number
    rightScore: number
    boundaryCueScore: number
    fullRowScore: number
    ratioScore: number
    crossBoundaryPenalty: number
  }
}

interface SplitToken {
  text: string
  normalized: string
  splitBoundaryWordIndex: number | null
}

interface SplitCandidateScore {
  afterWordIndex: number
  targetBoundaryOffset: number
  score: number
  metrics: LedgerSafeSplitLocalizationResult['metrics']
}

const DEFAULT_MINIMUM_SCORE = 0.66
const DEFAULT_MINIMUM_MARGIN = 0.045
const DEFAULT_HIGH_CONFIDENCE_SCORE = 0.82
const DEFAULT_HIGH_CONFIDENCE_MARGIN = 0.08
const ARABIC_DIACRITICS_PATTERN = /[\u0610-\u061a\u064b-\u065f\u0670\u06d6-\u06ed]/g

export const normalizeLedgerSafeSplitToken = (value: string): string => value
  .normalize('NFKC')
  .toLowerCase()
  .replace(ARABIC_DIACRITICS_PATTERN, '')
  .replace(/[إأٱآا]/g, 'ا')
  .replace(/[ى]/g, 'ي')
  .replace(/[ة]/g, 'ه')
  .replace(/[ؤئ]/g, 'ء')
  .replace(/ـ/g, '')
  .replace(/[^\p{Letter}\p{Number}]+/gu, '')
  .trim()

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value))

const collectSplitTokens = (context: LedgerSafeUnitSplitContext): SplitToken[] =>
  context.words
    .filter(word => word.segmentableWordIndex >= 0)
    .map(word => ({
      text: word.text,
      normalized: normalizeLedgerSafeSplitToken(word.text),
      splitBoundaryWordIndex: word.splitBoundaryWordIndex
    }))
    .filter(token => token.normalized.length > 0)

const buildText = (tokens: SplitToken[]): string =>
  tokens.map(token => token.text).join(' ').replace(/\s+/g, ' ').trim()

const countIntersection = (leftTokens: string[], rightTokens: string[]): number => {
  const rightCounts = new Map<string, number>()
  rightTokens.forEach(token => {
    rightCounts.set(token, (rightCounts.get(token) || 0) + 1)
  })
  let intersection = 0
  leftTokens.forEach(token => {
    const count = rightCounts.get(token) || 0
    if (count > 0) {
      intersection += 1
      rightCounts.set(token, count - 1)
    }
  })
  return intersection
}

const diceScore = (leftTokens: string[], rightTokens: string[]): number => {
  // mutation-ignore-next-line equivalent: one-sided empty inputs still produce a zero intersection and zero score.
  if (leftTokens.length === 0 || rightTokens.length === 0) {
    return 0
  }
  return (2 * countIntersection(leftTokens, rightTokens)) / (leftTokens.length + rightTokens.length)
}

const containmentScore = (leftTokens: string[], rightTokens: string[]): number => {
  if (leftTokens.length === 0 || rightTokens.length === 0) {
    return 0
  }
  return countIntersection(leftTokens, rightTokens) / Math.min(leftTokens.length, rightTokens.length)
}

const orderedCoverageScore = (patternTokens: string[], candidateTokens: string[]): number => {
  if (patternTokens.length === 0 || candidateTokens.length === 0) {
    return 0
  }
  let candidateIndex = 0
  let matched = 0
  patternTokens.forEach(patternToken => {
    while (candidateIndex < candidateTokens.length && candidateTokens[candidateIndex] !== patternToken) {
      candidateIndex += 1
    }
    if (candidateIndex < candidateTokens.length) {
      matched += 1
      candidateIndex += 1
    }
  })
  return matched / patternTokens.length
}

const sideScore = (sourceTokens: string[], targetTokens: string[]): number => Math.max(
  diceScore(sourceTokens, targetTokens),
  containmentScore(sourceTokens, targetTokens) * 0.94,
  orderedCoverageScore(sourceTokens, targetTokens) * 0.88
)

const cueScore = (sourceCue: string[], targetCue: string[]): number => Math.max(
  diceScore(sourceCue, targetCue),
  containmentScore(sourceCue, targetCue) * 0.96,
  orderedCoverageScore(sourceCue, targetCue) * 0.92
)

const takeSuffix = (tokens: string[], size: number): string[] =>
  tokens.slice(Math.max(0, tokens.length - size))

const takePrefix = (tokens: string[], size: number): string[] =>
  tokens.slice(0, size)

const computeCandidateScore = ({
  sourceTokens,
  targetTokens,
  sourceBoundaryOffset,
  targetBoundaryOffset,
  afterWordIndex
}: {
  sourceTokens: string[]
  targetTokens: string[]
  sourceBoundaryOffset: number
  targetBoundaryOffset: number
  afterWordIndex: number
}): SplitCandidateScore => {
  const sourceLeft = sourceTokens.slice(0, sourceBoundaryOffset)
  const sourceRight = sourceTokens.slice(sourceBoundaryOffset)
  const targetLeft = targetTokens.slice(0, targetBoundaryOffset)
  const targetRight = targetTokens.slice(targetBoundaryOffset)
  const leftScore = sideScore(sourceLeft, targetLeft)
  const rightScore = sideScore(sourceRight, targetRight)
  const leftCueScore = cueScore(takeSuffix(sourceLeft, 4), takeSuffix(targetLeft, 6))
  const rightCueScore = cueScore(takePrefix(sourceRight, 4), takePrefix(targetRight, 6))
  const boundaryCueScore = (leftCueScore + rightCueScore) / 2
  const fullRowScore = sideScore(sourceTokens, targetTokens)
  const sourceRatio = sourceBoundaryOffset / sourceTokens.length
  const targetRatio = targetBoundaryOffset / targetTokens.length
  const ratioScore = clamp01(1 - Math.abs(sourceRatio - targetRatio) * 2.25)
  const crossBoundaryPenalty = Math.max(
    cueScore(takeSuffix(sourceLeft, 4), takePrefix(targetRight, 6)),
    cueScore(takePrefix(sourceRight, 4), takeSuffix(targetLeft, 6))
  )
  const score = clamp01(
    (leftScore * 0.23) +
    (rightScore * 0.23) +
    (boundaryCueScore * 0.30) +
    (fullRowScore * 0.14) +
    (ratioScore * 0.10) -
    (crossBoundaryPenalty * 0.16)
  )

  return {
    afterWordIndex,
    targetBoundaryOffset,
    score,
    metrics: {
      leftScore,
      rightScore,
      boundaryCueScore,
      fullRowScore,
      ratioScore,
      crossBoundaryPenalty
    }
  }
}

export const locateLedgerSafeSplitSuggestion = ({
  sourceContext,
  targetContext,
  sourceSplitBoundaryWordIndex,
  minimumScore = DEFAULT_MINIMUM_SCORE,
  minimumMargin = DEFAULT_MINIMUM_MARGIN,
  highConfidenceScore = DEFAULT_HIGH_CONFIDENCE_SCORE,
  highConfidenceMargin = DEFAULT_HIGH_CONFIDENCE_MARGIN
}: LedgerSafeSplitLocalizationInput): LedgerSafeSplitLocalizationResult | null => {
  const sourceTokenPayloads = collectSplitTokens(sourceContext)
  const targetTokenPayloads = collectSplitTokens(targetContext)
  // mutation-ignore-next-line equivalent: later boundary/candidate checks also reject one-sided short inputs.
  if (sourceTokenPayloads.length < 2 || targetTokenPayloads.length < 2) {
    return null
  }

  const sourceBoundaryTokenIndex = sourceTokenPayloads.findIndex(
    token => token.splitBoundaryWordIndex === sourceSplitBoundaryWordIndex
  )
  if (sourceBoundaryTokenIndex < 0 || sourceBoundaryTokenIndex >= sourceTokenPayloads.length - 1) {
    return null
  }
  const sourceBoundaryOffset = sourceBoundaryTokenIndex + 1
  const sourceTokens = sourceTokenPayloads.map(token => token.normalized)
  const targetTokens = targetTokenPayloads.map(token => token.normalized)
  const sourceBoundaryCueOverlap = cueScore(
    takeSuffix(sourceTokens.slice(0, sourceBoundaryOffset), 4),
    takePrefix(sourceTokens.slice(sourceBoundaryOffset), 4)
  )
  // mutation-ignore-next-line equivalent: cueScore jumps above this threshold for all full-overlap repeated-boundary cases.
  if (sourceBoundaryCueOverlap >= 0.92) {
    return null
  }

  const candidateScores = targetTokenPayloads
    .map((token, index) => ({ token, index }))
    .filter(({ token, index }) => token.splitBoundaryWordIndex !== null && index < targetTokenPayloads.length - 1)
    .map(({ token, index }) => computeCandidateScore({
      sourceTokens,
      targetTokens,
      sourceBoundaryOffset,
      targetBoundaryOffset: index + 1,
      afterWordIndex: Number(token.splitBoundaryWordIndex)
    }))
    .sort((left, right) => right.score - left.score)

  const best = candidateScores[0]
  if (!best) {
    return null
  }
  const secondBestScore = candidateScores[1]?.score ?? 0
  const margin = best.score - secondBestScore
  if (best.score < minimumScore || margin < minimumMargin) {
    return null
  }

  const confidence: LedgerSafeSplitSuggestionConfidence =
    best.score >= highConfidenceScore && margin >= highConfidenceMargin ? 'high' : 'medium'
  const sourceLeftTokens = sourceTokenPayloads.slice(0, sourceBoundaryOffset)
  const sourceRightTokens = sourceTokenPayloads.slice(sourceBoundaryOffset)
  const targetLeftTokens = targetTokenPayloads.slice(0, best.targetBoundaryOffset)
  const targetRightTokens = targetTokenPayloads.slice(best.targetBoundaryOffset)

  return {
    afterWordIndex: best.afterWordIndex,
    confidence,
    score: best.score,
    margin,
    sourceBoundaryOffset,
    targetBoundaryOffset: best.targetBoundaryOffset,
    sourceLeftText: buildText(sourceLeftTokens),
    sourceRightText: buildText(sourceRightTokens),
    targetLeftText: buildText(targetLeftTokens),
    targetRightText: buildText(targetRightTokens),
    metrics: best.metrics
  }
}

export const __ledgerSafeSplitSuggestionTestInternals = {
  containmentScore,
  countIntersection,
  cueScore,
  diceScore,
  orderedCoverageScore,
  sideScore,
}
